import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { vodsApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import ComingSoon from '../components/ComingSoon';
import { 
  Download, 
  ExternalLink, 
  Eye, 
  Calendar,
  Clock,
  CheckCircle,
  Loader2,
  Crown,
  RefreshCw as Sync,
  Link as LinkIcon,
  MessageSquare
} from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

export default function TwitchVODs() {
  const { user } = useAuth();
  const [selectedVODs, setSelectedVODs] = useState<Set<string>>(new Set());
  const [backingUpVODs, setBackingUpVODs] = useState<Set<string>>(new Set());
  const [includeChatByDefault, setIncludeChatByDefault] = useState(true);
  const [showBulkBackupConfirm, setShowBulkBackupConfirm] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showLoading, updateNotification, showInfo } = useNotificationsContext();

  const { data: twitchVODs, isLoading, refetch } = useQuery(
    'twitch-vods',
    () => vodsApi.getTwitchVODs().then(res => res.data.data)
  );

  const backupMutation = useMutation(vodsApi.backupTwitchVOD, {
    onSuccess: () => {
      queryClient.invalidateQueries('twitch-vods');
      queryClient.invalidateQueries('vods');
      queryClient.invalidateQueries('dashboard-stats');
      queryClient.invalidateQueries('dashboard-activity');
    },
  });

  const syncMutation = useMutation(vodsApi.syncVODs, {
    onSuccess: () => {
      queryClient.invalidateQueries('twitch-vods');
      queryClient.invalidateQueries('vods');
      queryClient.invalidateQueries('dashboard-stats');
      queryClient.invalidateQueries('dashboard-activity');
    },
  });

  const handleBackup = async (twitchVodId: string, vodTitle: string) => {
    // Add to backing up set
    setBackingUpVODs(prev => new Set(prev).add(twitchVodId));
    
    // Show loading notification
    const notificationId = showLoading(
      'Backing up VOD',
      `Starting backup of "${vodTitle}"`
    );

    try {
      const response = await backupMutation.mutateAsync(twitchVodId);
      
      // Check if the VOD is actually still processing in the background
      if (response?.data?.data?.status === 'uploading' || response?.data?.data?.status === 'processing') {
        // Update notification to show it's in progress
        updateNotification(notificationId, {
          type: 'info',
          title: 'VOD Backup Started',
          message: `"${vodTitle}" is being downloaded in the background. Check "My VODs" for progress.`,
          duration: 8000,
          action: {
            label: 'View Progress',
            onClick: () => {
              window.location.href = '/vods';
            }
          }
        });
      } else {
        // Only show success if the backup is actually complete
        updateNotification(notificationId, {
          type: 'success',
          title: 'VOD Backed Up Successfully',
          message: `"${vodTitle}" has been backed up to your library`,
          duration: 5000,
          action: {
            label: 'View in My VODs',
            onClick: () => {
              window.location.href = '/vods';
            }
          }
        });
        
        showSuccess('Backup Complete', `"${vodTitle}" is now available in your VOD library`);
      }
    } catch (error: any) {
      console.error('Backup error:', error);
      
      // Update notification to error
      updateNotification(notificationId, {
        type: 'error',
        title: 'Backup Failed',
        message: error?.response?.data?.error || 'Failed to backup VOD. Please try again.',
        duration: 8000
      });
    } finally {
      // Remove from backing up set
      setBackingUpVODs(prev => {
        const newSet = new Set(prev);
        newSet.delete(twitchVodId);
        return newSet;
      });
    }
  };

  const handleBulkBackupClick = () => {
    if (selectedVODs.size === 0) return;
    setShowBulkBackupConfirm(true);
  };

  const handleBulkBackupConfirm = async () => {
    setShowBulkBackupConfirm(false);

    let successCount = 0;
    let inProgressCount = 0;
    
    for (const vodId of selectedVODs) {
      try {
        const response = await backupMutation.mutateAsync(vodId);
        if (response?.data?.data?.status === 'uploading' || response?.data?.data?.status === 'processing') {
          inProgressCount++;
        } else {
          successCount++;
        }
      } catch (error) {
        console.error(`Failed to backup VOD ${vodId}:`, error);
      }
    }
    
    // Show appropriate notification based on results
    if (inProgressCount > 0) {
      showInfo(
        'VOD Backups Started',
        `${inProgressCount} VOD${inProgressCount > 1 ? 's are' : ' is'} being downloaded in the background. Check "My VODs" for progress.`
      );
    }
    if (successCount > 0) {
      showSuccess(
        'VOD Backups Complete',
        `${successCount} VOD${successCount > 1 ? 's' : ''} backed up successfully.`
      );
    }
    
    setSelectedVODs(new Set());
  };

  const handleSync = async () => {
    if (user?.role !== 'premium') {
      alert('Auto-sync is only available for Premium users.');
      return;
    }

    try {
      await syncMutation.mutateAsync();
    } catch (error) {
      console.error('Sync error:', error);
      alert('Failed to sync VODs. Please try again.');
    }
  };

  const toggleVODSelection = (vodId: string) => {
    const newSelection = new Set(selectedVODs);
    if (newSelection.has(vodId)) {
      newSelection.delete(vodId);
    } else {
      newSelection.add(vodId);
    }
    setSelectedVODs(newSelection);
  };

  const selectAll = () => {
    if (!twitchVODs?.vods) return;
    const unbackedUpVODs = twitchVODs.vods.filter(vod => !vod.isBackedUp);
    setSelectedVODs(new Set(unbackedUpVODs.map(vod => vod.id)));
  };

  const clearSelection = () => {
    setSelectedVODs(new Set());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (duration: string) => {
    // Duration comes in format like "1h23m45s"
    const match = duration.match(/(\d+h)?(\d+m)?(\d+s)?/);
    if (!match) return duration;
    
    const hours = match[1] ? parseInt(match[1]) : 0;
    const minutes = match[2] ? parseInt(match[2]) : 0;
    const seconds = match[3] ? parseInt(match[3]) : 0;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatViewCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse"></div>
        <div className="grid gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
              <div className="flex space-x-4">
                <div className="w-40 h-24 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const unbackedUpVODs = twitchVODs?.vods.filter(vod => !vod.isBackedUp) || [];
  const backedUpVODs = twitchVODs?.vods.filter(vod => vod.isBackedUp) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Twitch VODs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Backup your recent Twitch broadcasts
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="btn-secondary"
          >
            <Sync className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {user?.role === 'premium' && (
            <button
              onClick={handleSync}
              disabled={syncMutation.isLoading}
              className="btn-primary"
            >
              {syncMutation.isLoading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Syncing...
                </>
              ) : (
                <>
                  <Sync className="h-4 w-4 mr-2" />
                  Auto Sync
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Premium Features Banner */}
      {user?.role !== 'premium' && (
        <div className="card bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200 relative">
          <ComingSoon />
          <div className="flex items-center space-x-3">
            <Crown className="h-8 w-8 text-yellow-600" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-yellow-900">
                Upgrade to Premium for Auto-Sync
              </h3>
              <p className="text-sm text-yellow-800">
                Premium users get automatic VOD backup every 3 hours, plus 2-year retention.
              </p>
            </div>
            <button 
              disabled
              className="btn-primary bg-gray-400 cursor-not-allowed opacity-75"
              title="Premium upgrades coming soon!"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {unbackedUpVODs.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {selectedVODs.size} of {unbackedUpVODs.length} VODs selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={selectAll}
                  className="text-sm text-twitch-600 hover:text-twitch-700"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-600 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>
            {selectedVODs.size > 0 && (
              <button
                onClick={handleBulkBackupClick}
                disabled={backupMutation.isLoading}
                className="btn-primary"
              >
                {backupMutation.isLoading ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    Backing up...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Backup Selected ({selectedVODs.size})
                  </>
                )}
              </button>
            )}
          </div>
          
          {/* Chat Backup Toggle */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700">Include chat backup by default</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeChatByDefault}
                  onChange={(e) => setIncludeChatByDefault(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Chat logs will be saved as JSON files alongside video backups
            </p>
          </div>
        </div>
      )}

      {/* Available VODs */}
      {unbackedUpVODs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-gray-900">
            Available for Backup ({unbackedUpVODs.length})
          </h2>
          {unbackedUpVODs.map((vod) => (
            <div key={vod.id} className="card hover:shadow-md transition-shadow">
              <div className="flex space-x-4">
                {/* Selection Checkbox */}
                <div className="flex-shrink-0 flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedVODs.has(vod.id)}
                    onChange={() => toggleVODSelection(vod.id)}
                    className="h-4 w-4 text-twitch-600 focus:ring-twitch-500 border-gray-300 rounded"
                  />
                </div>

                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  <div className="w-40 h-24 bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={vod.thumbnail_url.replace('%{width}', '320').replace('%{height}', '180')}
                      alt={vod.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {vod.title}
                      </h3>
                      {vod.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {vod.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <a
                        href={vod.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="View on Twitch"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      {vod.isBackedUp ? (
                        <div className="flex space-x-2">
                          <button
                            disabled
                            className="btn-secondary opacity-75"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Backed Up
                          </button>
                          <a
                            href="/vods"
                            className="btn-outline"
                            title="View in My VODs"
                          >
                            <LinkIcon className="h-4 w-4 mr-2" />
                            View Backup
                          </a>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleBackup(vod.id, vod.title)}
                          disabled={backingUpVODs.has(vod.id)}
                          className="btn-primary"
                        >
                          {backingUpVODs.has(vod.id) ? (
                            <Loader2 className="animate-spin h-4 w-4" />
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Backup
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(vod.created_at)}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{formatDuration(vod.duration)}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      <span>{formatViewCount(vod.view_count)} views</span>
                    </div>
                    
                    <div className="flex items-center">
                      <span className="capitalize">{vod.type}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Already Backed Up VODs */}
      {backedUpVODs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-gray-900">
            Already Backed Up ({backedUpVODs.length})
          </h2>
          {backedUpVODs.map((vod) => (
            <div key={vod.id} className="card bg-green-50 border-green-200">
              <div className="flex space-x-4">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  <div className="w-40 h-24 bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={vod.thumbnail_url.replace('%{width}', '320').replace('%{height}', '180')}
                      alt={vod.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {vod.title}
                      </h3>
                      {vod.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {vod.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center space-x-2 ml-4">
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="h-5 w-5 mr-1" />
                        <span className="text-sm font-medium">Backed Up</span>
                      </div>
                      <a
                        href={vod.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="View on Twitch"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(vod.created_at)}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{formatDuration(vod.duration)}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      <span>{formatViewCount(vod.view_count)} views</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span>Backup: {vod.backupSize}</span>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <MessageSquare className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {vod.chatBackupSize ? `Chat: ${vod.chatBackupSize}` : 'No chat'}
                          </span>
                        </div>
                        {includeChatByDefault && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            +Chat
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!twitchVODs?.vods.length && (
        <div className="card text-center py-12">
          <Download className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Twitch VODs found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Make sure you have recent broadcasts on your Twitch channel
          </p>
          <div className="mt-6">
            <button
              onClick={() => refetch()}
              className="btn-primary"
            >
              <Sync className="h-4 w-4 mr-2" />
              Refresh VODs
            </button>
          </div>
        </div>
      )}

      {/* Bulk Backup Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBulkBackupConfirm}
        onClose={() => setShowBulkBackupConfirm(false)}
        onConfirm={handleBulkBackupConfirm}
        title="Backup VODs"
        message={`Are you sure you want to backup ${selectedVODs.size} VOD${selectedVODs.size > 1 ? 's' : ''}? This will start the download and processing of the selected videos.`}
        confirmText={`Backup ${selectedVODs.size} VOD${selectedVODs.size > 1 ? 's' : ''}`}
        cancelText="Cancel"
        variant="default"
        isLoading={backupMutation.isLoading}
      />
    </div>
  );
}